import { Asset } from '@cube3/common/model/schema/resources/asset';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import {
  useCreateResource__ALPHA,
  useMappedId__ALPHA
} from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { uuidv4 } from '@cube3/state/src/utils/uuid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cardConfig } from '../config/cardConfig';
import { getErrorMessage } from '@cube3/forms/src/utils/getErrorMessage';

export type GenerationSettingsType = {
  prompt: string;
  seed?: number;
  aspect_ratio: string;
};

export type GenerationConfig = {
  settings: GenerationSettingsType;
  referenceAssets?: Asset[];
  referenceGeneration?: string;
  referenceBase64?: string;
};

export const useGenerateImage = () => {
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string>(null);

  const [workspaceId] = useCurrentWorkspace();

  const temporaryId = useRef(uuidv4()).current;
  const generationId = useMappedId__ALPHA(temporaryId);

  const [createResource, createResourceStatus, createResourceError] =
    useCreateResource__ALPHA({
      ancestor: { type: 'workspace', id: workspaceId },
      resourceType: 'generation',
      cacheInvalidator: null
    });

  const models = useResourceList__ALPHA({
    resourceType: 'workspace',
    resourceId: workspaceId,
    edgeType: 'generative-model',
    edgeLabel: 'generative-models'
  });

  const {
    resource: generation,
    retrieve,
    status: generationStatus,
    errors
  } = useResource__ALPHA({
    resourceType: 'generation',
    resourceId: generationId
  });

  const onGenerate = useCallback(
    ({
      settings,
      referenceAssets,
      referenceGeneration,
      referenceBase64
    }: GenerationConfig) => {
      const model = models.resources?.[0];
      if (!model) return;

      createResource({
        type: 'generation',
        temporaryId: temporaryId,
        ...settings,
        seed: Number(settings.seed) || 0,
        reference_base64: referenceGeneration ? null : referenceBase64,
        relationships: {
          model: {
            data: {
              type: 'generative-model',
              id: model.id
            }
          },
          reference_assets: referenceBase64
            ? null
            : !referenceGeneration && referenceAssets?.length
            ? {
                data: referenceAssets.map((ra) => ({
                  type: 'asset',
                  id: ra.id
                }))
              }
            : null,
          reference_generation: referenceGeneration
            ? {
                data: {
                  type: 'generation',
                  id: referenceGeneration
                }
              }
            : null
        }
      });
      setError(null);
      setPolling(true);
    },
    [models.resources, createResource]
  );

  useEffect(() => {
    if (createResourceStatus === statuses.FAILED && createResourceError) {
      setPolling(false);
      const err =
        getErrorMessage(createResourceError?.[0] || createResourceError) ||
        'Request failed.';
      setError(err);
    }
  }, [createResourceStatus, createResourceError]);

  useEffect(() => {
    if (generation && generation.status === 'failed') {
      setPolling(false);
      setError(cardConfig.error.content);
    }
  }, [generation]);

  useEffect(() => {
    if (generationStatus === statuses.FAILED && errors?.length) {
      setPolling(false);
      const err = getErrorMessage(errors[0]) || 'Request failed.';
      setError(err);
    }
  }, [generationStatus, errors]);

  // polling for the generated image
  useEffect(() => {
    if (createResourceStatus === statuses.SUCCESS && polling) {
      if (!generation?.url || generation.status !== 'completed') {
        const timer = setTimeout(() => retrieve(), 3000);
        return () => clearTimeout(timer);
      } else {
        setPolling(false);
      }
    }
  }, [createResourceStatus, polling, generation]);

  return {
    generation,
    onGenerate,
    error,
    setError,
    loading: models.loading || polling
  };
};
